import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { DEFAULT_LANGUAGE, toContentLocales } from './constants';
import { resolveI18n, resolveI18nList } from './resolveI18n';

export interface I18nResolvers {
  /**
   * Resolves a locale map of strings (e.g. `data.descriptionI18N`) to the entry
   * matching the current language. Returns `''` when the map holds nothing
   * usable and `null` when the value itself is absent.
   */
  resolveI18n: (value: unknown) => string | null;
  /**
   * Resolves a locale map of lists (e.g. `data.improvementsI18N`) to the entry
   * matching the current language. Returns `null` whenever no usable list is
   * found, so a legacy untranslated field can be picked up with `??`.
   */
  resolveI18nList: (value: unknown) => Array<string> | null;
}

/**
 * Resolvers for locale maps stored inside JSONB columns, bound to the language
 * currently selected in the app.
 *
 * The returned object is stable for a given language and the component
 * re-renders when the language changes, so resolved values update without a
 * refetch — unlike top-level `*I18N` columns, which are resolved server-side
 * and require the Apollo store reset done in `ExtendedApolloProvider`.
 *
 * @example
 * const { resolveI18n, resolveI18nList } = useI18nResolvers();
 * // ...
 * <Text>{resolveI18n(info.data?.descriptionI18N) || info.data?.description}</Text>
 * const improvements =
 *   resolveI18nList(page.data?.improvementsI18N) ?? page.data?.improvements ?? [];
 */
const useI18nResolvers = (): I18nResolvers => {
  const { i18n } = useTranslation();

  // Same chain the `Accept-Language` header carries, so a locale map nested in
  // a JSONB column resolves exactly like a column the API resolved server-side.
  const languages = toContentLocales(i18n.language ?? DEFAULT_LANGUAGE);

  return React.useMemo(
    () => ({
      resolveI18n: (value: unknown) => resolveI18n(value, languages),
      resolveI18nList: (value: unknown) => resolveI18nList(value, languages),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [languages.join(',')],
  );
};

export default useI18nResolvers;
